import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Heading,
  Button,
  HStack,
  Badge,
  IconButton,
  useToast,
  Text,
  Image,
  useDisclosure,
  ButtonGroup,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Select,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiEye, FiList, FiMap, FiRefreshCw, FiSettings } from 'react-icons/fi';
import DataTable, { Column } from '@/components/common/DataTable';
import apiClient, { Device, Tenant } from '@/services/letsparkyClient';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from 'react-i18next';
import DeviceDetailsModal from '@/components/devices/DeviceDetailsModal';
import DeviceMap from '@/components/devices/DeviceMap';
import InitializeDeviceModal from '@/components/devices/InitializeDeviceModal';
import AddDeviceModal from '@/components/devices/AddDeviceModal';

const Devices: React.FC = () => {
  const toast = useToast();
  const { user, token } = useAuthStore();
  const isSystem = user?.role === 'SYSTEM';
  const { t } = useTranslation();

  // Modal states
  const { isOpen: isDetailsModalOpen, onOpen: onDetailsModalOpen, onClose: onDetailsModalClose } = useDisclosure();
  const { isOpen: isFactoryResetOpen, onOpen: onFactoryResetOpen, onClose: onFactoryResetClose } = useDisclosure();
  const { isOpen: isInitializeDeviceOpen, onOpen: onInitializeDeviceOpen, onClose: onInitializeDeviceClose } = useDisclosure();
  const { isOpen: isAddDeviceOpen, onOpen: onAddDeviceOpen, onClose: onAddDeviceClose } = useDisclosure();

  // Data states
  const [devices, setDevices] = useState<Device[]>([]);
  const [allDevices, setAllDevices] = useState<Device[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [deviceForAction, setDeviceForAction] = useState<Device | null>(null);

  // UI states
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'map'>('table');
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [statusFilter, setStatusFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');

  const cancelRef = useRef<HTMLButtonElement>(null);

  // Fetch tenants for filter dropdown (system users only)
  useEffect(() => {
    if (!isSystem || !token) return;

    const fetchTenants = async () => {
      try {
        apiClient.client.setToken(token);
        const response = await apiClient.tenants.list({ limit: 1000 });
        if (response?.data?.data) {
          setTenants(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching tenants:', error);
      }
    };

    fetchTenants();
  }, [isSystem, token]);

  // Main data fetching
  useEffect(() => {
    if (!token) return;

    const fetchDevices = async () => {
      setIsLoading(true);
      try {
        apiClient.client.setToken(token);

        const params: Record<string, string> = {};
        
        if (!isSystem && user?.tenantId) {
          params.tenant_id = user.tenantId;
        }
        if (searchQuery.trim()) {
          params.search = searchQuery.trim();
        }
        if (statusFilter) {
          params.status = statusFilter;
        }
        if (tenantFilter && isSystem) {
          params.tenant_id = tenantFilter;
        }
        if (sortBy) {
          params.sort_by = sortBy;
          params.sort_direction = sortDirection;
        }

        const response = await apiClient.devices.listPaginated(
          { page: currentPage, limit: pageSize },
          params
        );

        setDevices(response.data);
        setTotalItems(response.pagination.total);

        // Fetch all devices for map view
        if (viewMode === 'map') {
          const allResponse = await apiClient.devices.listPaginated(
            { page: 1, limit: 2000 },
            params
          );
          setAllDevices(allResponse.data);
        }
      } catch (error) {
        toast({
          title: 'Error fetching devices',
          description: error instanceof Error ? error.message : 'Unknown error',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDevices();
  }, [token, currentPage, pageSize, searchQuery, statusFilter, tenantFilter, sortBy, sortDirection, viewMode, isSystem, user?.tenantId, toast]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSort = (newSortBy: string, newSortDirection: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortDirection(newSortDirection);
    setCurrentPage(1);
  };

  const handleViewDevice = (device: Device) => {
    setSelectedDevice(device);
    onDetailsModalOpen();
  };

  const handleDeviceUpdated = () => {
    setCurrentPage(currentPage); // Trigger refetch
    onDetailsModalClose();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this device?')) return;

    try {
      apiClient.client.setToken(token!);
      await apiClient.devices.delete(id);
      toast({
        title: 'Device deleted',
        status: 'success',
        duration: 3000,
      });
      setCurrentPage(1); // Trigger refetch
    } catch (error) {
      toast({
        title: 'Error deleting device',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleFactoryReset = async () => {
    if (!deviceForAction) return;

    try {
      apiClient.client.setToken(token!);
      await apiClient.devices.factoryReset(deviceForAction.id);

      toast({
        title: 'Factory reset successful',
        description: `Device ${deviceForAction.nick_name || deviceForAction.serial_no} has been reset`,
        status: 'success',
        duration: 3000,
      });

      onFactoryResetClose();
      setCurrentPage(currentPage); // Trigger refetch
    } catch (error) {
      toast({
        title: 'Factory reset failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const openFactoryResetModal = (device: Device) => {
    setDeviceForAction(device);
    onFactoryResetOpen();
  };

  const openInitializeDeviceModal = (device: Device) => {
    setDeviceForAction(device);
    onInitializeDeviceOpen();
  };

  const handleInitializeDeviceSuccess = () => {
    setCurrentPage(currentPage); // Trigger refetch
  };

  const handleAddDeviceSuccess = () => {
    setCurrentPage(currentPage); // Trigger refetch
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'green';
      case 'offline': return 'red';
      case 'maintenance': return 'orange';
      default: return 'gray';
    }
  };

  const calculateBatteryPercentage = (voltage: number) => {
    const percentage = Math.round(((voltage - 5000) / (6500 - 5000)) * 100);
    return Math.min(percentage, 100);
  };

  const getBatteryColor = (voltage: number) => {
    const percentage = calculateBatteryPercentage(voltage);
    if (percentage < 20) return 'red';
    if (percentage < 50) return 'orange';
    if (percentage < 70) return '#52aa20';
    return '#20ee20';
  };

  const columns: Column<Device>[] = [
    { header: 'Name', accessor: 'nick_name', sortable: true, sortKey: 'nick_name' },
    { header: 'Serial Number', accessor: 'serial_no', sortable: true, sortKey: 'serial_no' },
    { header: 'IMEI Number', accessor: 'imei_number', sortable: true, sortKey: 'imei_number' },
    { header: 'Short ID', accessor: 'qr_data', sortable: true, sortKey: 'qr_data' },
    {
      header: 'Photo',
      accessor: (device: Device) =>
        device.photo ? (
          <Box w="100px" h="80px" overflow="hidden">
            <Image src={device.photo} alt={device.nick_name || 'Device'} w="100%" h="100%" objectFit="cover" />
          </Box>
        ) : null,
    },
    {
      header: 'Status',
      accessor: (device) => (
        <Badge colorScheme={getStatusColor(device.status)}>{device.status}</Badge>
      ),
      sortable: true,
      sortKey: 'status',
    },
    {
      header: 'Sensor',
      accessor: (device) => (
        <Badge colorScheme={device.sensor ? 'blue' : 'gray'}>{`${device.sensor}`}</Badge>
      ),
      sortable: true,
      sortKey: 'sensor',
    },
    {
      header: 'Location',
      accessor: (device) => device.location ? <Text color="gray.500">{device.location}</Text> : <Text color="gray.500">-</Text>,
      sortable: true,
      sortKey: 'location',
    },
    ...(isSystem ? [{
      header: 'Tenant',
      accessor: (device: Device) => {
        const tenant = tenants.find(t => t.id === device.tenant_id);
        return tenant ? <Text>{tenant.name}</Text> : null;
      },
      sortable: true,
      sortKey: 'tenant_id',
    }] : []),
    {
      header: 'Battery (mV)',
      accessor: (device) =>
        device.battery_voltage ? (
          <Badge color={getBatteryColor(device.battery_voltage)}>
            {device.battery_voltage} ({calculateBatteryPercentage(device.battery_voltage)}%)
          </Badge>
        ) : null,
      sortable: true,
      sortKey: 'battery_voltage',
    },
    {
      header: 'Last Seen',
      accessor: (device) =>
        device.last_connection_date
          ? new Date(device.last_connection_date).toLocaleString()
          : <Text color="gray.500">-</Text>,
      sortable: true,
      sortKey: 'last_connection_date',
    },
  ];

  const actionButtons = (device: Device) => (
    <>
      <IconButton
        aria-label="View device details"
        icon={<FiEye />}
        size="sm"
        colorScheme="blue"
        variant="ghost"
        onClick={() => handleViewDevice(device)}
        mr={1}
      />
      {(isSystem || user?.role === 'TENANT_ADMIN') && (
        <>
          <IconButton
            aria-label="Initialize device"
            icon={<FiSettings />}
            size="sm"
            colorScheme="green"
            variant="ghost"
            onClick={() => openInitializeDeviceModal(device)}
            mr={1}
          />
          <IconButton
            aria-label="Factory reset"
            icon={<FiRefreshCw />}
            size="sm"
            colorScheme="orange"
            variant="ghost"
            onClick={() => openFactoryResetModal(device)}
            mr={1}
          />
        </>
      )}
      {isSystem && (
        <IconButton
          aria-label="Delete device"
          icon={<FiTrash2 />}
          size="sm"
          colorScheme="red"
          variant="ghost"
          onClick={() => handleDelete(device.id)}
        />
      )}
    </>
  );

  return (
    <Box>
      <HStack justifyContent="space-between" mb={6}>
        <Heading size="lg">{t('devices.title')}</Heading>
        <HStack spacing={4}>
          <ButtonGroup size="sm" isAttached variant="outline">
            <Button
              leftIcon={<FiList />}
              onClick={() => setViewMode('table')}
              colorScheme={viewMode === 'table' ? 'blue' : 'gray'}
              variant={viewMode === 'table' ? 'solid' : 'outline'}
            >
              Table
            </Button>
            <Button
              leftIcon={<FiMap />}
              onClick={() => setViewMode('map')}
              colorScheme={viewMode === 'map' ? 'blue' : 'gray'}
              variant={viewMode === 'map' ? 'solid' : 'outline'}
            >
              Map
            </Button>
          </ButtonGroup>
          {isSystem && (
            <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={onAddDeviceOpen}>
              Add Device
            </Button>
          )}
        </HStack>
      </HStack>

      {/* Simple Filters */}
      <HStack mb={4} spacing={4}>
        {/* <Select
          placeholder="All statuses"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          maxW="200px"
        >
          <option value="online">Online</option>
          <option value="offline">Offline</option>
          <option value="maintenance">Maintenance</option>
        </Select> */}
        {isSystem && (
          <Select
            placeholder="All tenants"
            value={tenantFilter}
            onChange={(e) => { setTenantFilter(e.target.value); setCurrentPage(1); }}
            maxW="200px"
          >
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
            ))}
          </Select>
        )}
      </HStack>

      {viewMode === 'table' ? (
        <DataTable
          columns={columns}
          data={devices}
          keyField="id"
          isLoading={isLoading}
          totalItems={totalItems}
          currentPage={currentPage}
          pageSize={pageSize}
          sort_by={sortBy}
          sort_direction={sortDirection}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearch={handleSearch}
          onSort={handleSort}
          actionButtons={actionButtons}
        />
      ) : (
        <DeviceMap
          devices={allDevices}
          isLoading={isLoading}
          onDeviceView={handleViewDevice}
          onDeviceEdit={isSystem ? handleViewDevice : undefined}
          height="calc(100vh - 200px)"
        />
      )}

      <DeviceDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={onDetailsModalClose}
        device={selectedDevice}
        onDeviceUpdated={handleDeviceUpdated}
      />

      <AlertDialog
        isOpen={isFactoryResetOpen}
        leastDestructiveRef={cancelRef}
        onClose={onFactoryResetClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Factory Reset Device
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to factory reset device{' '}
              <strong>{deviceForAction?.nick_name || deviceForAction?.serial_no}</strong>?
              This action will remove all device settings and data.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onFactoryResetClose}>Cancel</Button>
              <Button colorScheme="red" onClick={handleFactoryReset} ml={3}>Factory Reset</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <InitializeDeviceModal
        isOpen={isInitializeDeviceOpen}
        onClose={onInitializeDeviceClose}
        device={deviceForAction}
        onSuccess={handleInitializeDeviceSuccess}
      />

      <AddDeviceModal
        isOpen={isAddDeviceOpen}
        onClose={onAddDeviceClose}
        onSuccess={handleAddDeviceSuccess}
      />
    </Box>
  );
};

export default Devices;
